import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  Tabs,
  Tab,
  Stack,
  Tooltip,
  Grid
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  LinkedIn as LinkedInIcon,
  Close as XIcon,
  Facebook as FacebookIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Link as LinkIcon,
  OpenInNew
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';

const ShareSurveyDialog = ({ open, onClose, survey }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);

  // Update the surveyUrl construction to match the routing
  const surveyUrl = `${window.location.origin}/app/form/${survey?.id}`;


  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(surveyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyEmbedCode = async () => {
    const embedCode = `<iframe 
  src="${window.location.origin}/app/form/${survey?.id}"
  width="100%" 
  height="600" 
  style="border: none; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"
  title="Survey Form"
></iframe>`;

    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Add preview function
  const handlePreviewSurvey = () => {
    window.open(surveyUrl, '_blank');
  };

  const handleShare = (platform) => {
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(surveyUrl)}&text=${encodeURIComponent('Take my survey: ' + survey?.name)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(surveyUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(surveyUrl)}`,
      email: `mailto:?subject=${encodeURIComponent('Take my survey: ' + survey?.name)}&body=${encodeURIComponent(`I'd like to share this survey with you: ${surveyUrl}`)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`Take my survey: ${survey?.name}\n${surveyUrl}`)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(surveyUrl)}&text=${encodeURIComponent('Take my survey: ' + survey?.name)}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  const handleDownloadQR = async () => {
    const qrNode = document.getElementById('survey-qr-code');
    if (qrNode) {
      try {
        const dataUrl = await toPng(qrNode);
        const link = document.createElement('a');
        link.download = `${survey?.name || 'survey'}-qr-code.png`;
        link.href = dataUrl;
        link.click();
      } catch (err) {
        console.error('QR code download failed:', err);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: '900px',
          minHeight: 'auto',
          maxWidth: '90vw',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          pb: 0,
          '& .MuiTypography-root': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Share Survey
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 400 }}>
            {survey?.name}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              py: 2,
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              minWidth: 120
            }
          }}
        >
          <Tab label="Share" />
          <Tab label="Embed" />
        </Tabs>
      </Box>

      <DialogContent
        sx={{
          p: 3,
          overflow: 'auto',
          '& .MuiGrid-root': {
            marginBottom: 0
          }
        }}
      >
        {activeTab === 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Box sx={{ mb: { xs: 3, md: 4 } }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Share Link
                </Typography>
                <TextField
                  fullWidth
                  value={surveyUrl}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title={copied ? "Copied!" : "Copy link"}>
                          <IconButton onClick={handleCopyLink} color="primary">
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Preview survey">
                          <IconButton
                            onClick={handlePreviewSurvey}
                            sx={{
                              ml: 1,
                              color: 'primary.main',
                              '&:hover': { bgcolor: 'primary.lighter' }
                            }}
                          >
                            <OpenInNew />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ),
                    sx: {
                      bgcolor: 'grey.50',
                      '& input': { fontFamily: 'monospace' }
                    }
                  }}
                />
              </Box>

              <Box sx={{ mb: { xs: 2, md: 0 }, display: 'flex', justifyContent: 'center' }}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    '& .MuiIconButton-root': {
                      width: 45,
                      height: 45,
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      }
                    }
                  }}
                >
                  <Tooltip title="Share on LinkedIn">
                    <IconButton
                      onClick={() => handleShare('linkedin')}
                      sx={{
                        color: '#0077B5',
                        '&:hover': { bgcolor: '#0077B520' }
                      }}
                    >
                      <LinkedInIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share on X">
                    <IconButton
                      onClick={() => handleShare('twitter')}
                      sx={{
                        color: '#000000',
                        '&:hover': { bgcolor: '#00000020' }
                      }}
                    >
                      <XIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share on Facebook">
                    <IconButton
                      onClick={() => handleShare('facebook')}
                      sx={{
                        color: '#4267B2',
                        '&:hover': { bgcolor: '#4267B220' }
                      }}
                    >
                      <FacebookIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share via WhatsApp">
                    <IconButton
                      onClick={() => handleShare('whatsapp')}
                      sx={{
                        color: '#25D366',
                        '&:hover': { bgcolor: '#25D36620' }
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share via Telegram">
                    <IconButton
                      onClick={() => handleShare('telegram')}
                      sx={{
                        color: '#0088cc',
                        '&:hover': { bgcolor: '#0088cc20' }
                      }}
                    >
                      <TelegramIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share via Email">
                    <IconButton
                      onClick={() => handleShare('email')}
                      sx={{
                        color: '#EA4335',
                        '&:hover': { bgcolor: '#EA433520' }
                      }}
                    >
                      <EmailIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy Link">
                    <IconButton
                      onClick={handleCopyLink}
                      sx={{
                        color: 'text.primary',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <LinkIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{
                p: 3,
                height: '100%',
                minHeight: { xs: 'auto', md: '300px' },
                bgcolor: 'grey.50',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  QR Code
                </Typography>
                <Box sx={{
                  p: 3,
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.1)'
                }}>
                  <div id="survey-qr-code">
                    <QRCodeSVG
                      value={surveyUrl}
                      size={180}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleDownloadQR}
                  sx={{ mt: 3 }}
                >
                  Download QR Code
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Embed this survey on your website
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={`<iframe 
  src="${window.location.origin}/app/form/${survey?.id}"
  width="100%" 
  height="600" 
  style="border: none; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"
  title="Survey Form"
></iframe>`}
              InputProps={{
                readOnly: true,
                sx: {
                  fontFamily: 'monospace',
                  bgcolor: 'grey.50',
                  fontSize: '14px',
                  '& .MuiInputBase-input': {
                    lineHeight: '1.6',
                    padding: '16px',
                  }
                },
                endAdornment: (
                  <Tooltip title={copied ? "Copied!" : "Copy code"}>
                    <IconButton
                      onClick={handleCopyEmbedCode}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'background.paper',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareSurveyDialog;
